import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Mail, Phone, MapPin, Clock, ArrowRight, CheckCircle, Linkedin, Twitter, Facebook, Instagram, Loader2, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import { FAQ } from "./FAQ";
import { contactus } from "../../apihandlers";

type SubjectOption = {
  value: string;
  label: string;
};

const SUBJECT_OPTIONS: SubjectOption[] = [
  { value: "project", label: "Project Inquiry" },
  { value: "consultation", label: "General Consultation" },
  { value: "partnership", label: "Partnership" },
  { value: "career", label: "Careers" },
  { value: "other", label: "Other" },
];

export function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!selectedSubject) {
      toast.error("Please select a subject");
      return;
    }

    setIsSubmitting(true);
    
    const form = e.target as HTMLFormElement;

    try {
      await contactus({
        firstName: (form.elements.namedItem('firstName') as HTMLInputElement)?.value,
        lastName: (form.elements.namedItem('lastName') as HTMLInputElement)?.value,
        email: (form.elements.namedItem('email') as HTMLInputElement)?.value,
        phone: (form.elements.namedItem('phone') as HTMLInputElement)?.value,
        subject: selectedSubject,
        message: (form.elements.namedItem('message') as HTMLTextAreaElement)?.value,
      });
      setIsSubmitting(false);
      toast.success("Message sent successfully!", {
        description: "We'll get back to you within 24 hours.",
        duration: 5000,
      });

    } catch (e: any) {
      setIsSubmitting(false);
      console.log(e);
      toast.error("Something went wrong, please try again later!");
    }

    // Reset form
    form.reset();
    setSelectedSubject("");
  };

  const selectedOptionLabel = SUBJECT_OPTIONS.find(opt => opt.value === selectedSubject)?.label;

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1667430806405-70ef5bc4970f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBjdXN0b21lciUyMHN1cHBvcnQlMjBtaW5pbWFsaXN0fGVufDF8fHx8MTc3MTUyNDQ0MXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral" 
            alt="Contact Intelisine" 
            className="w-full h-full object-cover blur-sm"
          />
          <div className="absolute inset-0 bg-slate-900/80"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#FD5750] font-bold tracking-widest uppercase mb-4 block">Get in Touch</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Let's Start a <span className="text-[#FD5750]">Conversation</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Whether you have a specific project in mind or just want to explore how we can help your business grow, we're here to listen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Contact Information</h2>
              <p className="text-slate-600 mb-10 leading-relaxed">
                Reach out to us directly or fill out the form. Our team is ready to answer any questions about our services, process, or pricing.
              </p>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center text-[#FD5750] shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">Our Office</h3>
                    <p className="text-slate-600">
                      No. 8, B6, Sri Krishna Flats, <br/>
                      10th Street, Nanganallur, Chennai-600061 <br/>
                      Tamil Nadu, India
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center text-[#FD5750] shrink-0">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">Email Us</h3>
                    <p className="text-slate-600">
                      <a href="mailto:hello@intelisine.com" className="hover:text-[#FD5750] transition-colors">hello@intelisine.com</a><br />
                      <a href="mailto:support@intelisine.com" className="hover:text-[#FD5750] transition-colors">support@intelisine.com</a>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center text-[#FD5750] shrink-0">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">Call Us</h3>
                    <p className="text-slate-600">
                      <a href="tel:+916383250771" className="hover:text-[#FD5750] transition-colors">+91 63832 50771</a><br />
                      <span className="text-sm text-slate-400">Mon-Fri 9am - 6pm PST</span>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center text-[#FD5750] shrink-0">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">Business Hours</h3>
                    <p className="text-slate-600">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Weekend: Closed
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a href="#" className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-[#FD5750] hover:text-white transition-all">
                    <Linkedin size={20} />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-[#FD5750] hover:text-white transition-all">
                    <Twitter size={20} />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-[#FD5750] hover:text-white transition-all">
                    <Facebook size={20} />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-[#FD5750] hover:text-white transition-all">
                    <Instagram size={20} />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-slate-50 p-8 md:p-10 rounded-3xl shadow-sm border border-slate-100"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h3>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">First Name</label>
                    <input 
                      type="text"
                      name="firstName"
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FD5750] focus:border-[#FD5750] outline-none transition-all"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Last Name</label>
                    <input 
                      type="text"
                      name="lastName"
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FD5750] focus:border-[#FD5750] outline-none transition-all"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
                  <input 
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FD5750] focus:border-[#FD5750] outline-none transition-all"
                    placeholder="john@company.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number (Optional)</label>
                  <input 
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FD5750] focus:border-[#FD5750] outline-none transition-all"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Subject</label>
                  <div className="relative" ref={dropdownRef}>
                    {/* Hidden input for form validation compatibility if needed, though we check manually on submit */}
                    <input type="hidden" name="subject" value={selectedSubject} required />
                    
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className={`w-full px-4 py-3 bg-white border rounded-lg outline-none transition-all flex items-center justify-between text-left ${
                        isDropdownOpen 
                          ? "border-[#FD5750] ring-2 ring-[#FD5750]" 
                          : "border-slate-200 focus:border-[#FD5750] focus:ring-2 focus:ring-[#FD5750]"
                      }`}
                    >
                      <span className={selectedSubject ? "text-slate-900" : "text-slate-400"}>
                        {selectedSubject ? selectedOptionLabel : "Select a topic"}
                      </span>
                      <motion.div
                        animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown size={20} className="text-slate-500" />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 w-full mt-2 bg-white border border-slate-100 rounded-lg shadow-xl z-20 overflow-hidden"
                        >
                          <ul className="max-h-60 overflow-y-auto py-1">
                            {SUBJECT_OPTIONS.map((option) => (
                              <li key={option.value}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedSubject(option.value);
                                    setIsDropdownOpen(false);
                                  }}
                                  className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors flex items-center justify-between ${
                                    selectedSubject === option.value ? "bg-slate-50 text-[#FD5750] font-medium" : "text-slate-700"
                                  }`}
                                >
                                  {option.label}
                                  {selectedSubject === option.value && (
                                    <Check size={16} className="text-[#FD5750]" />
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Message</label>
                  <textarea
                    name="message"
                    rows={4}
                    required
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FD5750] focus:border-[#FD5750] outline-none transition-all resize-none"
                    placeholder="Tell us about your project or inquiry..."
                  ></textarea>
                </div>
                
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-slate-900 text-white font-bold rounded-lg hover:bg-[#FD5750] transition-colors shadow-lg flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Send Message <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Embedded FAQ Section */}
      <FAQ />
    </div>
  );
}
